"use strict";

/**
 * <cart-checkout>
 * Reliability + progression feedback for the cart Checkout CTA.
 *
 * Background: a third-party Terms & Conditions app (tncapp.com) injects its own
 * gating logic intended for the mini-cart. On the /cart page that same script
 * can mis-bind and leave the Checkout button dead (it never submits the cart
 * form). This component guarantees the /cart Checkout button always works:
 *
 *  - We own the click. On click we run our own prerequisite check (the theme's
 *    required terms checkbox). If it passes we submit the cart form directly
 *    via requestSubmit()/submit(), which cannot be swallowed by a stray
 *    third-party submit-cancel or a pointer-events/overlay trick.
 *  - If a prerequisite fails we SURFACE it (inline message + focus) instead of
 *    silently doing nothing.
 *  - We defensively clear states a blocker app may have left on the button
 *    (disabled attribute, pointer-events:none) so the button is always live.
 *  - The button can never get stuck in the spinner/locked state (failsafe).
 *
 * We intentionally never set the native `disabled` attribute on submit, because
 * a disabled submit button is excluded from form data and would drop the
 * required `checkout` parameter.
 */
(function () {
  if (customElements.get("cart-checkout")) {
    return;
  }

  function ensureStyles() {
    if (document.getElementById("pe-cart-checkout-styles")) {
      return;
    }
    var s = document.createElement("style");
    s.id = "pe-cart-checkout-styles";
    s.textContent =
      ".cart-ctas{position:relative;z-index:2;isolation:isolate}" +
      ".cart-checkout-button{position:relative;z-index:2;pointer-events:auto!important;touch-action:manipulation}" +
      ".cart-dynamic-checkout-buttons{position:relative;z-index:1}";
    (document.head || document.documentElement).appendChild(s);
  }

  class CartCheckout extends HTMLElement {
    connectedCallback() {
      ensureStyles();

      this.form = this.closest("form") || document.getElementById("cart");
      this.button =
        this.querySelector("[data-checkout-button]") || document.getElementById("checkout");

      if (!this.form || !this.button || this.initialized) {
        return;
      }

      this.initialized = true;
      this.submitting = false;

      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onInvalid = this.onInvalid.bind(this);
      this.onPageShow = this.onPageShow.bind(this);

      // Own the click in the CAPTURE phase so we run before any third-party
      // click handler that might try to cancel/stop it.
      this.button.addEventListener("click", this.onClick, true);
      this.form.addEventListener("submit", this.onSubmit);
      window.addEventListener("pageshow", this.onPageShow);

      // Keep the button live even if an app tries to disable / block it.
      this.keepAlive();
      this._aliveTimer = window.setInterval(() => this.keepAlive(), 750);

      this.getPrerequisites().forEach((field) => {
        field.addEventListener("invalid", this.onInvalid);
        field.addEventListener("change", () => {
          if (field.checkValidity()) {
            this.clearFieldError(field);
          }
        });
      });
    }

    disconnectedCallback() {
      this.button?.removeEventListener("click", this.onClick, true);
      this.form?.removeEventListener("submit", this.onSubmit);
      window.removeEventListener("pageshow", this.onPageShow);
      window.clearInterval(this._aliveTimer);
    }

    /**
     * Undo the common ways a third-party script can neutralize the button.
     */
    keepAlive() {
      if (!this.button) return;
      if (this.button.disabled && this.form && this.form.querySelector) {
        // Only force-enable when the cart is not actually empty.
        var hasItems = !this.classList.contains("is-empty") &&
          !document.querySelector("#main-cart-footer.is-empty");
        if (hasItems) {
          this.button.disabled = false;
        }
      }
      var st = this.button.style;
      if (st && st.pointerEvents === "none") {
        st.pointerEvents = "auto";
      }
      this.button.removeAttribute("aria-disabled");
    }

    getPrerequisites() {
      const fields = new Set();
      this.querySelectorAll("[required], [data-checkout-prerequisite]").forEach((el) =>
        fields.add(el),
      );
      if (this.form && this.form.elements) {
        Array.from(this.form.elements).forEach((el) => {
          if (el.required || el.hasAttribute("data-checkout-prerequisite")) {
            fields.add(el);
          }
        });
      }
      return Array.from(fields);
    }

    /** Our own prerequisite gate (theme terms checkbox). */
    prerequisitesPass() {
      var ok = true;
      var firstBad = null;
      this.getPrerequisites().forEach((field) => {
        if (typeof field.checkValidity === "function" && !field.checkValidity()) {
          ok = false;
          if (!firstBad) firstBad = field;
          this.showFieldError(field);
        }
      });
      if (!ok && firstBad) {
        try { firstBad.focus({ preventScroll: true }); } catch (e) { firstBad.focus(); }
        if (typeof firstBad.scrollIntoView === "function") {
          firstBad.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      return ok;
    }

    /**
     * Authoritative click handler. Runs in capture phase, so even if a
     * third-party script later calls preventDefault on the bubbling click,
     * we have already taken over and submitted the form ourselves.
     */
    onClick(event) {
      if (this.submitting) {
        event.preventDefault();
        return;
      }
      // Gate on our own prerequisites; if they fail, stop and surface.
      if (!this.prerequisitesPass()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // Prerequisites pass -> submit the cart form ourselves, bypassing any
      // third-party handler that might try to cancel the default submit.
      event.preventDefault();
      event.stopPropagation();
      this.submitCart();
    }

    submitCart() {
      if (this.submitting) return;
      this.setLoading(true);
      var form = this.form;
      // Ensure the required `checkout` param is present even though we are
      // submitting programmatically (the named button is not auto-included).
      try {
        if (!form.querySelector('input[name="checkout"]')) {
          var hidden = document.createElement("input");
          hidden.type = "hidden";
          hidden.name = "checkout";
          hidden.value = "";
          form.appendChild(hidden);
        }
      } catch (e) {}
      try {
        if (typeof form.requestSubmit === "function") {
          form.requestSubmit();
        } else {
          form.submit();
        }
      } catch (e) {
        try { form.submit(); } catch (e2) {}
      }
    }

    onSubmit(event) {
      if (this.submitting) {
        // Allow our own programmatic submit through.
        return;
      }
      // A native submit (e.g. Enter key) — run the same gate.
      if (!this.prerequisitesPass()) {
        event.preventDefault();
        return;
      }
      this.setLoading(true);
    }

    onInvalid(event) {
      const field = event.target;
      if (!this.getErrorElement(field)) {
        return;
      }
      event.preventDefault();
      this.showFieldError(field);
    }

    getErrorElement(field) {
      if (field.id) {
        const byFor = document.querySelector(`[data-error-for="${field.id}"]`);
        if (byFor) {
          return byFor;
        }
      }
      const describedBy = field.getAttribute("aria-describedby");
      return describedBy ? document.getElementById(describedBy) : null;
    }

    showFieldError(field) {
      const target = this.getErrorElement(field);
      const message =
        field.getAttribute("data-error-message") ||
        field.validationMessage ||
        "This field is required to continue to checkout.";
      if (target) {
        target.textContent = message;
        target.hidden = false;
      }
      field.setAttribute("aria-invalid", "true");
      const wrapper = field.closest("label") || field.parentElement;
      wrapper?.classList.add("cart-checkout-prerequisite--invalid");
    }

    clearFieldError(field) {
      const target = this.getErrorElement(field);
      if (target) {
        target.textContent = "";
        target.hidden = true;
      }
      field.removeAttribute("aria-invalid");
      const wrapper = field.closest("label") || field.parentElement;
      wrapper?.classList.remove("cart-checkout-prerequisite--invalid");
    }

    setLoading(isLoading) {
      this.submitting = isLoading;
      this.button.classList.toggle("cart-checkout-button--loading", isLoading);
      this.button.setAttribute("aria-busy", isLoading ? "true" : "false");
      window.clearTimeout(this._stuckTimer);
      if (isLoading) {
        this._stuckTimer = window.setTimeout(() => this.reset(), 6000);
      }
    }

    reset() {
      this.setLoading(false);
    }

    onPageShow(event) {
      if (event.persisted) {
        this.reset();
      }
    }
  }

  customElements.define("cart-checkout", CartCheckout);
})();
